/**
 * Installer Onboarding Wizard — IN-2.b + IN-2.c
 * ─────────────────────────────────────────────
 * Self-service wizard shell. Contractor-facing onboarding UI.
 *
 * IN-2.b: token entry, identity, service-area.
 * IN-2.c: document upload (metadata-only) + agreement signature.
 *
 * Hard rules carried in:
 *   • No final submit, no payout setup, no dispatch enforcement.
 *   • No binary persisted to localStorage — uploads store only
 *     fileName / fileSize / mimeType / uploadedAt metadata.
 *   • Observer mode disables every write action.
 *
 * Anchor: RECONCILIATION.md §17 (IN-2.b), §18 (IN-2.c)
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import {
  getOnboardingSteps,
  getNextStepKey,
  getPreviousStepKey,
  type OnboardingStepKey,
} from "@/data/contractor/onboarding/stepRegistry";
import { getReuploadResumeStep } from "@/data/contractor/onboarding/adminReview";
import {
  formatOnboardingStepKeys,
  getOnboardingStepLabel,
} from "@/data/contractor/onboarding/stepLabels";
import {
  deriveSessionLifecycleStage,
  ONBOARDING_LIFECYCLE_LABELS,
} from "@/data/contractor/onboarding/lifecycle";
import {
  isInviteTokenUsable,
} from "@/data/contractor/onboarding/inviteTokens";
import {
  areRequiredDocumentsUploaded,
  getOnboardingDocumentsForSession,
  getRequiredOnboardingDocumentTypes,
  type OnboardingDocumentType,
} from "@/data/contractor/onboarding/documents";
import { hasRequiredAgreementSignature } from "@/data/contractor/onboarding/onboardingAgreements";
import { hasValidOnboardingPayout, type OnboardingPayoutType } from "@/data/contractor/onboarding/payout";
import { OnboardingSavedIndicator } from "@/components/contractor/onboarding/OnboardingSavedIndicator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getRequiredOnboardingCompliance } from "@/data/contractor/compliance/complianceRequirements";
import type {
  ContractorOnboardingDraft,
  OnboardingSession,
} from "@/data/contractor/onboarding/types";
import { hasAnyObserverIdentity } from "@/data/observerMode/observerSession";
import { OnboardingErrorBoundary } from "@/components/onboarding/OnboardingErrorBoundary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import {
  AlertCircle as AlertCircleIcon,
  HelpCircle as HelpCircleIcon,
  Clock as ClockIcon,
  Ban as BanIcon,
  Check as CheckIcon,
  Eye as EyeIcon,
  ShieldCheck as ShieldCheckIcon,
  Save as SaveIcon,
  CheckCircle2 as CheckCircle2Icon,
  Circle as CircleIcon,
  Loader2 as Loader2Icon,
  LifeBuoy as LifeBuoyIcon,
  ArrowRight as ArrowRightIcon,
  RefreshCw as RefreshCwIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";


const ACTOR = "invitee";

// Sprint A.2 — distinct invite failure shells.
type InviteFailureKind = "invalid" | "expired" | "revoked" | "consumed" | "observer";

type TokenState =
  | { kind: "loading" }
  | { kind: "invalid"; failure: InviteFailureKind; reason?: string }
  | { kind: "ready"; session: OnboardingSession };

interface NoticeShellConfig {
  badge: string;
  title: string;
  body: string;
  cta: string;
  helper: string;
  tone: "neutral" | "warning" | "destructive" | "info" | "success";
  icon: typeof AlertCircleIcon;
}

const NOTICE_SHELLS: Record<InviteFailureKind, NoticeShellConfig> = {
  invalid: {
    badge: "Link not recognized",
    title: "Invite not recognized",
    body: "We can't match this link to any onboarding invite on file. It may be mistyped, forwarded, or no longer valid.",
    cta: "Request a fresh onboarding invite",
    helper: "Forward this page to your Koredor administrator so they can re-issue your invite.",
    tone: "warning",
    icon: HelpCircleIcon,
  },
  expired: {
    badge: "Expired invite",
    title: "This invite has expired",
    body: "Onboarding links expire to keep your information safe. Ask your Koredor administrator to issue a new one — it only takes a moment.",
    cta: "Request a fresh onboarding invite",
    helper: "Once a new link is sent, you can pick up exactly where you left off.",
    tone: "warning",
    icon: ClockIcon,
  },
  revoked: {
    badge: "Cancelled by administrator",
    title: "This invite was cancelled by an administrator.",
    body: "A Koredor administrator cancelled this onboarding invite. If this was unexpected, reach out to confirm next steps.",
    cta: "Contact your Koredor administrator",
    helper: "Cancelled invites can't be reactivated — a new invite will be issued if needed.",
    tone: "destructive",
    icon: BanIcon,
  },
  consumed: {
    badge: "Already used",
    title: "This invite has already been used.",
    body: "This onboarding packet has already been completed. If you need to make changes, your administrator can issue a follow-up step.",
    cta: "Continue onboarding or sign in",
    helper: "If you're trying to resume, use the original link or contact your administrator.",
    tone: "success",
    icon: CheckIcon,
  },
  observer: {
    badge: "Observer session",
    title: "You're observing another account",
    body: "Onboarding writes are disabled while observing. You can read the wizard, but nothing you do will be saved to this invite.",
    cta: "Exit observer mode",
    helper: "Switch back to your own session to resume editing.",
    tone: "neutral",
    icon: EyeIcon,
  },
};

const NOTICE_TONE_CLASSES: Record<NoticeShellConfig["tone"], string> = {
  neutral: "bg-muted/60 text-foreground border-border",
  warning: "bg-warning/15 text-warning-foreground border-warning/40",
  destructive: "bg-destructive/15 text-destructive border-destructive/40",
  info: "bg-info/15 text-info border-info/40",
  success: "bg-success/15 text-success border-success/40",
};

const NOTICE_ICON_TONE: Record<NoticeShellConfig["tone"], string> = {
  neutral: "bg-muted text-muted-foreground ring-border",
  warning: "bg-warning/15 text-warning ring-warning/30",
  destructive: "bg-destructive/15 text-destructive ring-destructive/30",
  info: "bg-info/15 text-info ring-info/30",
  success: "bg-success/15 text-success ring-success/30",
};

function InviteNoticeShell({
  failure,
  detail,
}: {
  failure: InviteFailureKind;
  detail?: string;
}) {
  const cfg = NOTICE_SHELLS[failure];
  const Icon = cfg.icon;
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6">
      <Card
        className="max-w-md w-full overflow-hidden shadow-sm"
        data-testid={`onboarding-notice-${failure}`}
      >
        {/* Branded header strip */}
        <div className="border-b bg-card px-5 py-3 flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-primary text-primary-foreground grid place-items-center text-[10px] font-semibold">
            K
          </div>
          <span className="text-xs font-medium tracking-tight text-foreground">
            Koredor · Installer Onboarding
          </span>
        </div>

        <CardHeader className="pb-3">
          <div className="flex flex-col items-start gap-3">
            <div
              className={`h-12 w-12 rounded-full ring-1 grid place-items-center ${NOTICE_ICON_TONE[cfg.tone]}`}
              aria-hidden
            >
              <Icon className="h-6 w-6" />
            </div>
            <div
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${NOTICE_TONE_CLASSES[cfg.tone]}`}
              data-testid={`onboarding-notice-tone-${failure}`}
            >
              {cfg.badge}
            </div>
            <CardTitle
              data-testid="onboarding-error-title"
              className="text-lg leading-snug"
            >
              {cfg.title}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p className="text-muted-foreground leading-relaxed">{cfg.body}</p>
          {detail ? (
            <p
              className="text-xs text-muted-foreground/80 rounded-md border bg-muted/40 px-2.5 py-1.5"
              data-testid="onboarding-notice-detail"
            >
              {detail}
            </p>
          ) : null}
          <Button
            type="button"
            variant={cfg.tone === "destructive" ? "destructive" : "default"}
            size="sm"
            className="w-full"
            data-testid={`onboarding-notice-cta-${failure}`}
            onClick={() => {
              if (typeof window !== "undefined") {
                window.location.href =
                  "mailto:onboarding@koredor.app?subject=Onboarding%20invite%20help";
              }
            }}
          >
            {cfg.cta}
            <ArrowRightIcon className="h-3.5 w-3.5 ml-1.5" />
          </Button>
          <div className="flex items-start gap-2 pt-2 border-t text-xs text-muted-foreground">
            <LifeBuoyIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{cfg.helper}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ProgressBar({
  total,
  completed,
  currentIndex,
}: {
  total: number;
  completed: number;
  currentIndex: number;
}) {
  return (
    <div className="flex items-center gap-2" aria-label="Onboarding progress">
      {Array.from({ length: total }).map((_, i) => {
        const done = i < completed;
        const active = i === currentIndex;
        return (
          <div
            key={i}
            className={
              "h-1.5 flex-1 rounded-full " +
              (done
                ? "bg-primary"
                : active
                  ? "bg-primary/40"
                  : "bg-muted")
            }
          />
        );
      })}
    </div>
  );
}

// IN-2.e — entity-aware copy. Shared step renderers; labels adapt by entityType.
function getIdentityCopy(entityType: OnboardingSession["entityType"]) {
  switch (entityType) {
    case "internal_w2":
      return {
        nameLabel: "Employee legal name",
        contactLabel: "Primary contact name",
        helper:
          "Internal Koredor team member. We collect employment details next.",
      };
    case "manufacturer_partner":
      return {
        nameLabel: "Partner organization name",
        contactLabel: "Program contact name",
        helper:
          "Manufacturer partner. We collect certification documents next.",
      };
    default:
      return {
        nameLabel: "Company / legal name",
        contactLabel: "Primary contact name",
        helper: null,
      };
  }
}

function IdentityStep({
  session,
  readOnly,
  onPatch,
}: {
  session: OnboardingSession;
  readOnly: boolean;
  onPatch: (patch: Partial<ContractorOnboardingDraft>) => void;
}) {
  const d = session.draft;
  const copy = getIdentityCopy(session.entityType);
  return (
    <div className="space-y-4" data-testid="onboarding-step-identity">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label htmlFor="ob-name">{copy.nameLabel}</Label>
          <Input
            id="ob-name"
            value={d.name ?? ""}
            onChange={(e) => onPatch({ name: e.target.value })}
            disabled={readOnly}
            data-testid="ob-name"
          />
        </div>
        <div>
          <Label htmlFor="ob-contact-name">{copy.contactLabel}</Label>
          <Input
            id="ob-contact-name"
            value={d.contactName ?? ""}
            onChange={(e) => onPatch({ contactName: e.target.value })}
            disabled={readOnly}
            data-testid="ob-contact-name"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ob-contact-email">Email</Label>
            <Input
              id="ob-contact-email"
              type="email"
              value={d.contactEmail ?? ""}
              onChange={(e) => onPatch({ contactEmail: e.target.value })}
              disabled={readOnly}
              data-testid="ob-contact-email"
            />
          </div>
          <div>
            <Label htmlFor="ob-contact-phone">Phone</Label>
            <Input
              id="ob-contact-phone"
              value={d.contactPhone ?? ""}
              onChange={(e) =>
                onPatch({ contactPhone: formatPhoneNumber(e.target.value) })
              }
              disabled={readOnly}
              data-testid="ob-contact-phone"
            />
          </div>
        </div>
      </div>

      <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
        <div>
          <span className="font-medium text-foreground">Entity type:</span>{" "}
          <span data-testid="ob-entitytype-readonly">{session.entityType}</span>
        </div>
        {session.parentEntityId ? (
          <div>
            <span className="font-medium text-foreground">Parent entity:</span>{" "}
            <span data-testid="ob-parent-readonly">{session.parentEntityId}</span>
          </div>
        ) : null}
        {copy.helper ? (
          <div data-testid="ob-identity-helper">{copy.helper}</div>
        ) : null}
      </div>
    </div>
  );
}

function ServiceAreaStep({
  session,
  readOnly,
  onPatch,
}: {
  session: OnboardingSession;
  readOnly: boolean;
  onPatch: (patch: Partial<ContractorOnboardingDraft>) => void;
}) {
  const d = session.draft;
  const regionsText = (d.serviceRegions ?? []).join(", ");
  const zipsText = (d.serviceZipCodes ?? []).join(", ");
  const isW2 = session.entityType === "internal_w2";
  return (
    <div className="space-y-4" data-testid="onboarding-step-service_area">
      <div>
        <Label htmlFor="ob-trade">
          {isW2 ? "Job title / role" : "Trade type"}
        </Label>
        <Input
          id="ob-trade"
          value={d.tradeType ?? ""}
          onChange={(e) => onPatch({ tradeType: e.target.value })}
          disabled={readOnly}
          placeholder={
            isW2 ? "e.g. Project Manager, Field Tech" : "e.g. Roofing, HVAC, Solar"
          }
          data-testid="ob-trade"
        />
      </div>
      <div>
        <Label htmlFor="ob-regions">
          {isW2
            ? "Assigned regions (comma-separated)"
            : "Service regions (comma-separated)"}
        </Label>
        <Textarea
          id="ob-regions"
          value={regionsText}
          onChange={(e) =>
            onPatch({
              serviceRegions: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          disabled={readOnly}
          rows={2}
          data-testid="ob-regions"
        />
      </div>
      {isW2 ? null : (
        <div>
          <Label htmlFor="ob-zips">Service ZIP codes (comma-separated)</Label>
          <Textarea
            id="ob-zips"
            value={zipsText}
            onChange={(e) =>
              onPatch({
                serviceZipCodes: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            disabled={readOnly}
            rows={2}
            data-testid="ob-zips"
          />
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {isW2
          ? "Used for scheduling and reporting. Optional."
          : "Optional. Leave blank if not applicable."}
      </p>
    </div>
  );
}

// ── IN-2.f — Parent / Branch / Crew / Helper shared steps ─────────
const CHILD_ENTITY_TYPES = new Set(["branch", "crew", "helper"]);
function isChildEntity(et: OnboardingSession["entityType"]): boolean {
  return CHILD_ENTITY_TYPES.has(et);
}

function getParentAccountabilityCopy(et: OnboardingSession["entityType"]): string {
  switch (et) {
    case "branch":
      return "Your branch operates under your parent's license, insurance, and W-9. Payouts route through the parent entity.";
    case "crew":
      return "Crew members are covered by the parent contractor's insurance and policies. The parent is accountable for your work and routes your pay.";
    case "helper":
      return "Helpers work under the supervision of the parent contractor. The parent is accountable for your work and handles all payouts.";
    default:
      return "";
  }
}

function ParentConfirmationStep({
  session,
}: {
  session: OnboardingSession;
}) {
  const parentId = session.parentEntityId ?? "";
  return (
    <div className="space-y-4" data-testid="onboarding-step-parent_confirmation">
      <p className="text-sm text-muted-foreground">
        You've been invited as a <span className="font-medium text-foreground">{session.entityType}</span>{" "}
        attached to a parent entity. This binding is set at invite time and
        cannot be edited here — it's how we keep accountability and payouts
        flowing correctly.
      </p>
      <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Parent entity
          </div>
          {parentId ? (
            <div
              className="font-medium"
              data-testid="ob-parent-confirmation-id"
            >
              {parentId}
            </div>
          ) : (
            <div
              className="text-destructive text-xs"
              data-testid="ob-parent-confirmation-missing"
            >
              Missing parent entity. Contact your Koredor admin — this invite
              cannot be completed without a parent binding.
            </div>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Accountability mode
          </div>
          <div className="font-medium" data-testid="ob-parent-confirmation-mode">
            parent
          </div>
        </div>
        <div
          className="text-xs text-muted-foreground"
          data-testid="ob-parent-confirmation-copy"
        >
          {getParentAccountabilityCopy(session.entityType)}
        </div>
      </div>
    </div>
  );
}

function InheritedCompliancePreviewStep({
  session,
}: {
  session: OnboardingSession;
}) {
  const onboarding = useOnboarding();
  const reqs = getRequiredOnboardingCompliance(session, onboarding.sessions);
  const inherited = [
    reqs.inheritsFromParent.license ? "Contractor license" : null,
    reqs.inheritsFromParent.insurance ? "General liability & WC insurance" : null,
    reqs.inheritsFromParent.w9 ? "IRS W-9" : null,
  ].filter(Boolean) as string[];
  const directRequired = [
    !reqs.inheritsFromParent.license && reqs.licenseRequired ? "Contractor license" : null,
    !reqs.inheritsFromParent.insurance && reqs.insuranceRequired ? "Insurance certificate" : null,
    !reqs.inheritsFromParent.w9 && reqs.w9Required ? "IRS W-9" : null,
    reqs.backgroundCheckRequired ? "Background check" : null,
  ].filter(Boolean) as string[];
  return (
    <div className="space-y-4" data-testid="onboarding-step-inherited_compliance_preview">
      <p className="text-sm text-muted-foreground">
        Here's what your parent already covers and what we'll still need from
        you directly.
      </p>
      <div className="rounded-md border bg-card p-3 space-y-2">
        <div className="text-sm font-medium">Inherited from parent</div>
        {inherited.length > 0 ? (
          <ul
            className="list-disc pl-5 text-xs text-muted-foreground"
            data-testid="ob-inherited-list"
          >
            {inherited.map((i) => (
              <li key={i} data-testid={`ob-inherited-item-${i}`}>
                {i} <span className="opacity-60">· read-only</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-xs text-muted-foreground">
            Nothing inherited for your entity type.
          </div>
        )}
      </div>
      <div className="rounded-md border bg-card p-3 space-y-2">
        <div className="text-sm font-medium">Still required directly</div>
        {directRequired.length > 0 ? (
          <ul
            className="list-disc pl-5 text-xs text-muted-foreground"
            data-testid="ob-direct-required-list"
          >
            {directRequired.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        ) : (
          <div
            className="text-xs text-muted-foreground"
            data-testid="ob-direct-required-empty"
          >
            Nothing required directly from you.
          </div>
        )}
      </div>
    </div>
  );
}

function BranchOverridesStep({
  session,
  readOnly,
  onPatch,
}: {
  session: OnboardingSession;
  readOnly: boolean;
  onPatch: (patch: Partial<ContractorOnboardingDraft>) => void;
}) {
  const d = session.draft;
  return (
    <div className="space-y-4" data-testid="onboarding-step-branch_overrides">
      <p className="text-sm text-muted-foreground">
        Optional. If your branch operates in a different region or trade
        focus than the parent, note it here. Leave blank to mirror the
        parent's defaults.
      </p>
      <div>
        <Label htmlFor="ob-branch-trade">Branch trade focus (optional)</Label>
        <Input
          id="ob-branch-trade"
          value={d.tradeType ?? ""}
          onChange={(e) => onPatch({ tradeType: e.target.value })}
          disabled={readOnly}
          placeholder="e.g. Solar (branch specialty)"
          data-testid="ob-branch-trade"
        />
      </div>
      <div>
        <Label htmlFor="ob-branch-regions">
          Branch service regions (comma-separated, optional)
        </Label>
        <Textarea
          id="ob-branch-regions"
          value={(d.serviceRegions ?? []).join(", ")}
          onChange={(e) =>
            onPatch({
              serviceRegions: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          disabled={readOnly}
          rows={2}
          data-testid="ob-branch-regions"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Banking, license, insurance, and W-9 stay routed through the parent.
      </p>
    </div>
  );
}

function MinimalInfoStep({
  session,
  readOnly,
  onPatch,
}: {
  session: OnboardingSession;
  readOnly: boolean;
  onPatch: (patch: Partial<ContractorOnboardingDraft>) => void;
}) {
  const d = session.draft;
  return (
    <div className="space-y-4" data-testid="onboarding-step-minimal_info">
      <p className="text-sm text-muted-foreground">
        Just the basics. As a helper, you work under the parent contractor —
        no insurance, license, W-9, or banking is collected from you.
      </p>
      <div>
        <Label htmlFor="ob-helper-role">Role on the crew (optional)</Label>
        <Input
          id="ob-helper-role"
          value={d.tradeType ?? ""}
          onChange={(e) => onPatch({ tradeType: e.target.value })}
          disabled={readOnly}
          placeholder="e.g. Apprentice, Ground crew"
          data-testid="ob-helper-role"
        />
      </div>
      <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
        Your parent contractor is accountable for your work and handles all
        payouts. You will not be paid directly by Koredor.
      </div>
    </div>
  );
}

function SubmittedShell({ session }: { session: OnboardingSession }) {
  const timeline: {
    key: string;
    label: string;
    helper: string;
    state: "done" | "current" | "upcoming";
  }[] = [
    {
      key: "submitted",
      label: "Submitted",
      helper: session.submittedAt
        ? new Date(session.submittedAt).toLocaleString()
        : "Just now",
      state: "done",
    },
    {
      key: "document_review",
      label: "Document Review",
      helper: "A Koredor admin verifies your uploads.",
      state: "current",
    },
    {
      key: "admin_approval",
      label: "Admin Approval",
      helper: "Final compliance sign-off.",
      state: "upcoming",
    },
    {
      key: "activation",
      label: "Activation",
      helper: "Portal access and dispatch eligibility unlock.",
      state: "upcoming",
    },
  ];
  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6"
      data-testid="onboarding-submitted-shell"
    >
      <Card className="max-w-md w-full overflow-hidden shadow-sm">
        <div className="border-b bg-card px-5 py-3 flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-primary text-primary-foreground grid place-items-center text-[10px] font-semibold">
            K
          </div>
          <span className="text-xs font-medium tracking-tight text-foreground">
            Koredor · Installer Onboarding
          </span>
        </div>
        <CardHeader className="pb-3">
          <div className="flex flex-col items-start gap-3">
            <div className="h-12 w-12 rounded-full ring-1 ring-success/30 bg-success/15 text-success grid place-items-center">
              <CheckCircle2Icon className="h-6 w-6" />
            </div>
            <CardTitle data-testid="onboarding-submitted-title" className="text-lg leading-snug">
              Submitted for review
            </CardTitle>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Thanks{session.draft.contactName ? `, ${session.draft.contactName}` : ""}.
              Your onboarding packet is in our queue. Here's what happens next:
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <ol
            className="relative space-y-4"
            data-testid="onboarding-submitted-timeline"
          >
            {timeline.map((step, i) => {
              const isLast = i === timeline.length - 1;
              const ringTone =
                step.state === "done"
                  ? "bg-success/15 text-success ring-success/40"
                  : step.state === "current"
                    ? "bg-primary/15 text-primary ring-primary/40"
                    : "bg-muted text-muted-foreground ring-border";
              const StepIcon =
                step.state === "done"
                  ? CheckCircle2Icon
                  : step.state === "current"
                    ? Loader2Icon
                    : CircleIcon;
              return (
                <li
                  key={step.key}
                  className="flex gap-3"
                  data-testid={`onboarding-submitted-step-${step.key}`}
                  data-state={step.state}
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`h-7 w-7 rounded-full ring-1 grid place-items-center shrink-0 ${ringTone}`}
                    >
                      <StepIcon
                        className={`h-3.5 w-3.5 ${step.state === "current" ? "animate-spin" : ""}`}
                      />
                    </div>
                    {!isLast ? (
                      <div className="w-px flex-1 bg-border mt-1 min-h-[18px]" />
                    ) : null}
                  </div>
                  <div className="flex-1 pb-1">
                    <div className="text-sm font-medium text-foreground flex items-center gap-2">
                      {step.label}
                      {step.state === "current" ? (
                        <Badge
                          variant="outline"
                          className="bg-primary/10 text-primary border-primary/30 text-[10px] px-1.5 py-0"
                        >
                          In Progress
                        </Badge>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {step.helper}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          <div
            className="rounded-md border bg-muted/40 px-3 py-2 text-xs flex items-start gap-2"
            data-testid="onboarding-submitted-sla"
          >
            <ClockIcon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
            <div>
              <div className="font-medium text-foreground">
                Typical review time: within 1 business day
              </div>
              <div className="text-muted-foreground mt-0.5">
                You'll be notified once review is complete. No portal access or
                dispatch eligibility is granted until then.
              </div>
            </div>
          </div>

          <div
            className="flex items-start gap-2 pt-2 border-t text-xs text-muted-foreground"
            data-testid="onboarding-submitted-help"
          >
            <LifeBuoyIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Need assistance? Contact your Koredor administrator.</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── IN-2.d — Payout step ──────────────────────────────────────────
const OWN_PAYOUT_OPTIONS: { value: OnboardingPayoutType; label: string; help: string }[] = [
  { value: "ach", label: "ACH bank transfer", help: "Direct deposit. We'll collect tokenized bank details after approval." },
  { value: "check", label: "Paper check", help: "Mailed to your business address. Slowest payout option." },
  { value: "stripe_connect", label: "Stripe Connect", help: "Payouts routed through Stripe. Linked after approval." },
];

function PayoutStep({
  session,
  readOnly,
  onSet,
}: {
  session: OnboardingSession;
  readOnly: boolean;
  onSet: (input: {
    type: OnboardingPayoutType;
    last4?: string;
    parentEntityId?: string;
  }) => void;
}) {
  const onboarding = useOnboarding();
  const reqs = getRequiredOnboardingCompliance(session, onboarding.sessions);
  const current = session.payoutMethod;
  const [selected, setSelected] = useState<OnboardingPayoutType>(
    current?.type ??
      (reqs.bankingMode === "payroll"
        ? "payroll"
        : reqs.bankingMode === "parent_routed"
          ? "parent_routed"
          : "ach"),
  );
  const [last4, setLast4] = useState(current?.last4 ?? "");
  const [error, setError] = useState<string | null>(null);

  if (reqs.bankingMode === "payroll") {
    return (
      <div className="space-y-4" data-testid="onboarding-step-payout">
        <div
          className="rounded-md border bg-muted/30 p-3 text-sm"
          data-testid="ob-payout-locked-payroll"
        >
          <div className="font-medium">Payroll (locked)</div>
          <div className="text-xs text-muted-foreground mt-1">
            W-2 employees are paid through Koredor payroll. No bank details are
            collected here.
          </div>
        </div>
        {error ? (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            data-testid="ob-payout-error"
            role="alert"
          >
            {error}
          </div>
        ) : null}
        <Button
          type="button"
          disabled={readOnly || current?.type === "payroll"}
          data-testid="ob-payout-confirm"
          onClick={() => {
            try {
              setError(null);
              onSet({ type: "payroll" });
            } catch (err) {
              setError((err as Error).message);
            }
          }}
        >
          {current?.type === "payroll" ? "Confirmed" : "Confirm payroll"}
        </Button>
      </div>
    );
  }

  if (reqs.bankingMode === "parent_routed") {
    const parentId = session.parentEntityId ?? "";
    return (
      <div className="space-y-4" data-testid="onboarding-step-payout">
        <div
          className="rounded-md border bg-muted/30 p-3 text-sm"
          data-testid="ob-payout-locked-parent"
        >
          <div className="font-medium">Routed through parent (locked)</div>
          <div className="text-xs text-muted-foreground mt-1">
            Your payouts route through your parent entity. No personal bank
            details are collected.
          </div>
          {parentId ? (
            <div className="text-xs mt-2">
              <span className="font-medium text-foreground">Parent entity:</span>{" "}
              <span data-testid="ob-payout-parent-id">{parentId}</span>
            </div>
          ) : (
            <div
              className="text-xs mt-2 text-destructive"
              data-testid="ob-payout-parent-missing"
            >
              Missing parent entity. Contact your Koredor admin — this invite
              needs a parent assignment before you can continue.
            </div>
          )}
        </div>
        {error ? (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            data-testid="ob-payout-error"
            role="alert"
          >
            {error}
          </div>
        ) : null}
        <Button
          type="button"
          disabled={readOnly || !parentId || current?.type === "parent_routed"}
          data-testid="ob-payout-confirm"
          onClick={() => {
            try {
              setError(null);
              onSet({ type: "parent_routed", parentEntityId: parentId });
            } catch (err) {
              setError((err as Error).message);
            }
          }}
        >
          {current?.type === "parent_routed" ? "Confirmed" : "Confirm parent routing"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="onboarding-step-payout">
      <p className="text-sm text-muted-foreground">
        Choose how you'd like to receive payouts. We collect a placeholder now;
        tokenized bank details are exchanged after admin approval. Never enter
        full account or routing numbers here.
      </p>
      <div className="space-y-2" role="radiogroup" aria-label="Payout method">
        {OWN_PAYOUT_OPTIONS.map((opt) => {
          const active = selected === opt.value;
          return (
            <label
              key={opt.value}
              className={
                "flex items-start gap-3 rounded-md border p-3 cursor-pointer " +
                (active ? "bg-primary/5 ring-1 ring-primary/40" : "bg-card")
              }
              data-testid={`ob-payout-option-${opt.value}`}
            >
              <input
                type="radio"
                name="ob-payout-type"
                value={opt.value}
                checked={active}
                disabled={readOnly}
                onChange={() => setSelected(opt.value)}
                className="mt-1"
                data-testid={`ob-payout-radio-${opt.value}`}
              />
              <div className="min-w-0">
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs text-muted-foreground">{opt.help}</div>
              </div>
            </label>
          );
        })}
      </div>
      <div>
        <Label htmlFor="ob-payout-last4">
          Last 4 digits of your account (optional placeholder)
        </Label>
        <Input
          id="ob-payout-last4"
          value={last4}
          maxLength={4}
          inputMode="numeric"
          pattern="\d{4}"
          onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
          disabled={readOnly}
          placeholder="1234"
          data-testid="ob-payout-last4"
        />
        <div className="text-xs text-muted-foreground mt-1">
          Last 4 only. No routing or full account numbers.
        </div>
      </div>
      {current ? (
        <div
          className="text-xs text-muted-foreground"
          data-testid="ob-payout-current"
        >
          Saved: {current.type}
          {current.last4 ? ` · last4 ${current.last4}` : ""}
          {current.verified ? " · placeholder verified" : " · pending verification"}
        </div>
      ) : null}
      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="ob-payout-error"
          role="alert"
        >
          {error}
        </div>
      ) : null}
      <Button
        type="button"
        disabled={readOnly}
        data-testid="ob-payout-save"
        onClick={() => {
          try {
            setError(null);
            onSet({ type: selected, last4: last4 || undefined });
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      >
        Save payout method
      </Button>
    </div>
  );
}

// ── IN-2.d — Review step ──────────────────────────────────────────
function ReviewStep({
  session,
  readiness,
}: {
  session: OnboardingSession;
  readiness: { ready: boolean; blockers: string[]; warnings: string[] };
}) {
  const onboarding = useOnboarding();
  const reqs = getRequiredOnboardingCompliance(session, onboarding.sessions);
  const d = session.draft;
  const childEntity = isChildEntity(session.entityType);
  const inheritedItems = [
    reqs.inheritsFromParent.license ? "Contractor license" : null,
    reqs.inheritsFromParent.insurance ? "Insurance" : null,
    reqs.inheritsFromParent.w9 ? "W-9" : null,
  ].filter(Boolean) as string[];
  return (
    <div className="space-y-4" data-testid="onboarding-step-review">
      <div className="rounded-md border bg-card p-3 space-y-1 text-sm">
        <div className="font-medium">Identity</div>
        <div className="text-xs text-muted-foreground">
          {d.name || "—"} · {d.contactName || "—"} · {d.contactEmail || "—"}
        </div>
      </div>
      {childEntity ? (
        <div
          className="rounded-md border bg-card p-3 space-y-1 text-sm"
          data-testid="ob-review-parent-block"
        >
          <div className="font-medium">Parent entity</div>
          <div className="text-xs text-muted-foreground">
            {session.parentEntityId || "—"} · accountability: parent · payout
            routed to parent
          </div>
          {inheritedItems.length > 0 ? (
            <div
              className="text-xs text-muted-foreground"
              data-testid="ob-review-inherited"
            >
              Inherited: {inheritedItems.join(", ")}
            </div>
          ) : null}
        </div>
      ) : null}
      {session.entityType === "manufacturer_partner" || childEntity ? null : (
        <div className="rounded-md border bg-card p-3 space-y-1 text-sm">
          <div className="font-medium">
            {session.entityType === "internal_w2" ? "Employment" : "Service area"}
          </div>
          <div className="text-xs text-muted-foreground">
            {d.tradeType || "Not specified"}
            {(d.serviceRegions ?? []).length > 0
              ? ` · ${(d.serviceRegions ?? []).join(", ")}`
              : ""}
            {(d.serviceZipCodes ?? []).length > 0
              ? ` · ZIPs: ${(d.serviceZipCodes ?? []).join(", ")}`
              : ""}
          </div>
        </div>
      )}
      <div className="rounded-md border bg-card p-3 space-y-1 text-sm">
        <div className="font-medium">Agreement</div>
        <div className="text-xs text-muted-foreground">
          {(session.signatures ?? []).find(
            (s) => s.variant === reqs.agreementVariant,
          )
            ? `Signed (${reqs.agreementVariant})`
            : "Not signed"}
        </div>
      </div>
      <div className="rounded-md border bg-card p-3 space-y-1 text-sm">
        <div className="font-medium">Payout</div>
        <div className="text-xs text-muted-foreground">
          {session.payoutMethod
            ? `${session.payoutMethod.type}${session.payoutMethod.last4 ? ` · last4 ${session.payoutMethod.last4}` : ""}${session.payoutMethod.verified ? " · placeholder verified" : " · pending verification"}`
            : "Not set"}
        </div>
      </div>

      {readiness.blockers.length > 0 ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 space-y-1"
          data-testid="ob-review-blockers"
          role="alert"
        >
          <div className="text-sm font-medium text-destructive">
            Resolve before submitting
          </div>
          <ul className="list-disc pl-5 text-xs text-destructive">
            {readiness.blockers.map((b) => (
              <li key={b} data-testid="ob-review-blocker">
                {b}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {readiness.warnings.length > 0 ? (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-1"
          data-testid="ob-review-warnings"
        >
          <div className="text-sm font-medium text-amber-700 dark:text-amber-300">
            Things to know
          </div>
          <ul className="list-disc pl-5 text-xs text-amber-700 dark:text-amber-300">
            {readiness.warnings.map((w) => (
              <li key={w} data-testid="ob-review-warning">
                {w}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {readiness.ready && readiness.warnings.length === 0 ? (
        <div
          className="text-xs text-muted-foreground"
          data-testid="ob-review-clear"
        >
          Everything looks ready. Submit to send your packet to a Koredor admin.
        </div>
      ) : null}
    </div>
  );
}

// ── IN-2.c — Documents step ───────────────────────────────────────
const DOCUMENT_LABELS: Record<OnboardingDocumentType, string> = {
  license: "Contractor license",
  insurance: "Insurance certificate",
  w9: "IRS W-9",
  background_check: "Background check",
  certification: "Manufacturer certification",
};

function DocumentsStep({
  session,
  readOnly,
  onUpload,
}: {
  session: OnboardingSession;
  readOnly: boolean;
  onUpload: (type: OnboardingDocumentType, file: File) => Promise<void> | void;
}) {
  const onboarding = useOnboarding();
  const { complianceDocuments } = onboarding;
  const required = getRequiredOnboardingDocumentTypes(session, onboarding.sessions);
  const uploaded = getOnboardingDocumentsForSession(complianceDocuments, session);
  const [error, setError] = useState<string | null>(null);

  if (required.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="onboarding-step-documents"
      >
        No compliance documents are required for your entity type.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="onboarding-step-documents">
      <p className="text-sm text-muted-foreground">
        Upload one file per category. Accepted: PDF, JPG, PNG, HEIC (max 10MB).
        Files are reviewed by your Koredor admin before approval.
      </p>
      {required.map((type) => {
        const has = uploaded.find((d) => d.type === type);
        const inputId = `ob-upload-${type}`;
        return (
          <div
            key={type}
            className="rounded-md border bg-card p-3 flex items-center justify-between gap-3"
            data-testid={`ob-doc-row-${type}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium">{DOCUMENT_LABELS[type]}</div>
              {has ? (
                <div
                  className="text-xs text-muted-foreground truncate"
                  data-testid={`ob-doc-meta-${type}`}
                >
                  {has.fileName} ·{" "}
                  {Math.round(
                    Number((has.metadata as { fileSize?: number } | undefined)?.fileSize ?? 0) /
                      1024,
                  )}
                  KB · pending verification
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">Not uploaded</div>
              )}
            </div>
            <div className="shrink-0">
              <input
                id={inputId}
                type="file"
                accept="application/pdf,image/jpeg,image/jpg,image/png,image/heic,image/heif"
                disabled={readOnly}
                data-testid={`ob-upload-${type}`}
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    setError(null);
                    await onUpload(type, file);
                  } catch (err) {
                    setError((err as Error).message);
                  } finally {
                    e.target.value = "";
                  }
                }}
              />
              <Button
                type="button"
                variant={has ? "ghost" : "default"}
                size="sm"
                disabled={readOnly}
                onClick={() => document.getElementById(inputId)?.click()}
                data-testid={`ob-upload-btn-${type}`}
              >
                {has ? "Replace" : "Upload"}
              </Button>
            </div>
          </div>
        );
      })}
      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="ob-doc-error"
          role="alert"
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}

// ── IN-2.c — Agreement step ───────────────────────────────────────
const AGREEMENT_LABELS: Record<string, string> = {
  "1099_msa": "1099 Master Service Agreement",
  w2_offer_letter: "W-2 Offer Letter",
  branch_sublicense: "Branch Sublicense Agreement",
  crew_acknowledgment: "Crew Acknowledgment",
  helper_acknowledgment: "Helper Acknowledgment",
  certification_agreement: "Manufacturer Certification Agreement",
};

const AGREEMENT_VERSION = "v1.9-R2";

function AgreementStep({
  session,
  readOnly,
  onSign,
}: {
  session: OnboardingSession;
  readOnly: boolean;
  onSign: (input: {
    variant: NonNullable<OnboardingSession["signatures"]>[number]["variant"];
    version: string;
    typedName: string;
  }) => void;
}) {
  const onboarding = useOnboarding();
  const reqs = getRequiredOnboardingCompliance(session, onboarding.sessions);
  const variant = reqs.agreementVariant;
  const already = (session.signatures ?? []).some((s) => s.variant === variant);
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expectedName = (session.draft.contactName ?? "").trim();

  if (already) {
    return (
      <div className="space-y-2" data-testid="onboarding-step-agreement">
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
          {AGREEMENT_LABELS[variant] ?? variant} signed.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="onboarding-step-agreement">
      <div className="rounded-md border bg-muted/20 p-3 text-sm">
        <div className="font-medium">
          {AGREEMENT_LABELS[variant] ?? variant}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          Version {AGREEMENT_VERSION}. By signing, you acknowledge you have
          read and agreed to the terms governing your participation in the
          Koredor installer network.
        </div>
      </div>

      <div className="flex items-start gap-2">
        <input
          id="ob-agreement-ack"
          type="checkbox"
          className="mt-1"
          checked={ack}
          disabled={readOnly}
          onChange={(e) => setAck(e.target.checked)}
          data-testid="ob-agreement-ack"
        />
        <Label
          htmlFor="ob-agreement-ack"
          className="text-sm font-normal leading-snug"
        >
          I have read and agree to the terms of the{" "}
          {AGREEMENT_LABELS[variant] ?? variant}.
        </Label>
      </div>

      <div>
        <Label htmlFor="ob-agreement-name">
          Type your full legal name to sign
        </Label>
        <Input
          id="ob-agreement-name"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={readOnly}
          placeholder={expectedName || "Full name"}
          data-testid="ob-agreement-name"
        />
        {expectedName ? (
          <div className="text-xs text-muted-foreground mt-1">
            Must match the contact name on file: {expectedName}
          </div>
        ) : null}
      </div>

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="ob-agreement-error"
          role="alert"
        >
          {error}
        </div>
      ) : null}

      <Button
        type="button"
        disabled={readOnly || !ack || !typed.trim()}
        data-testid="ob-agreement-sign"
        onClick={() => {
          try {
            setError(null);
            onSign({ variant, version: AGREEMENT_VERSION, typedName: typed });
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      >
        Sign agreement
      </Button>
    </div>
  );
}

function OnboardingWizardPageInner() {
  const { token: tokenParam } = useParams<{ token: string }>();
  const {
    inviteTokens,
    sessions,
    consumeInviteToken,
    updateOnboardingDraft,
    completeOnboardingStep,
  } = useOnboarding();

  const observerLocked = hasAnyObserverIdentity();
  const consumedRef = useRef(false);

  // Resolve token → session (idempotent in StrictMode by checking sessions first).
  const tokenState: TokenState = useMemo(() => {
    if (!tokenParam) return { kind: "invalid", failure: "invalid", reason: "Missing invite token." };
    const tok = inviteTokens.find((t) => t.token === tokenParam);
    if (!tok) return { kind: "invalid", failure: "invalid" };
    if (tok.status === "revoked") {
      return { kind: "invalid", failure: "revoked", reason: tok.revokedReason };
    }
    if (tok.status === "consumed") {
      const s = sessions.find((x) => x.tokenId === tok.id);
      if (s) return { kind: "ready", session: s };
      return { kind: "invalid", failure: "consumed" };
    }
    if (tok.status === "expired" || !isInviteTokenUsable(tok)) {
      return { kind: "invalid", failure: "expired" };
    }
    return { kind: "loading" };
  }, [tokenParam, inviteTokens, sessions]);

  useEffect(() => {
    if (tokenState.kind !== "loading") return;
    if (observerLocked) return;
    if (consumedRef.current) return;
    if (!tokenParam) return;
    consumedRef.current = true;
    try {
      consumeInviteToken(tokenParam, ACTOR);
    } catch {
      consumedRef.current = false;
    }
  }, [tokenState.kind, tokenParam, consumeInviteToken, observerLocked]);

  if (tokenState.kind === "loading") {
    if (observerLocked) {
      return <InviteNoticeShell failure="observer" />;
    }
    return (
      <div
        className="min-h-screen flex items-center justify-center text-sm text-muted-foreground"
        data-testid="onboarding-loading"
      >
        Loading invite…
      </div>
    );
  }
  if (tokenState.kind === "invalid") {
    return <InviteNoticeShell failure={tokenState.failure} detail={tokenState.reason} />;
  }

  return (
    <OnboardingWizardShell
      session={tokenState.session}
      readOnly={observerLocked}
    />
  );
}

export default function OnboardingWizardPage() {
  return (
    <OnboardingErrorBoundary>
      <OnboardingWizardPageInner />
    </OnboardingErrorBoundary>
  );
}

// ── Wizard shell (exported for test reuse, no router coupling) ────
export function OnboardingWizardShell({
  session,
  readOnly,
}: {
  session: OnboardingSession;
  readOnly: boolean;
}) {
  const {
    complianceDocuments,
    updateOnboardingDraft,
    completeOnboardingStep,
    submitOnboardingDocument,
    signOnboardingAgreement,
    setOnboardingPayout,
    getOnboardingSubmitReadiness,
    finalizeOnboardingSubmit,
  } = useOnboarding();

  const steps = getOnboardingSteps(session);
  // IN-2.g — when admin requested reupload, snap the wizard to the
  // first step that still needs rework on mount. Snapshot once; later
  // navigation is driven by the writer-updated `session.currentStep`.
  const initialKey = useMemo<OnboardingStepKey>(() => {
    const fromSession =
      session.status === "reupload_requested"
        ? getReuploadResumeStep(session)
        : session.currentStep;
    return (steps.find((s) => s.key === fromSession)?.key ??
      steps[0]?.key) as OnboardingStepKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [localStepKey, setLocalStepKey] = useState<OnboardingStepKey>(initialKey);
  // If the writer advances session.currentStep beyond what we hold
  // locally (e.g. completeOnboardingStep with nextStep), follow it.
  useEffect(() => {
    if (session.currentStep && session.currentStep !== localStepKey) {
      const valid = steps.find((s) => s.key === session.currentStep);
      // Don't clobber the reupload jump on the first effect tick.
      if (valid && session.status !== "reupload_requested") {
        setLocalStepKey(session.currentStep as OnboardingStepKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.currentStep]);
  const currentKey = localStepKey;
  const currentIndex = steps.findIndex((s) => s.key === currentKey);
  const currentDescriptor = steps[currentIndex];
  const isLast = currentIndex === steps.length - 1;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const docsSatisfied = areRequiredDocumentsUploaded(complianceDocuments, session);
  const agreementSigned = hasRequiredAgreementSignature(session);
  const payoutValid = hasValidOnboardingPayout(session);
  const readiness = getOnboardingSubmitReadiness(session.id);

  const continueDisabled =
    readOnly ||
    (currentKey === "documents" && !docsSatisfied) ||
    (currentKey === "agreement" && !agreementSigned) ||
    (currentKey === "payout" && !payoutValid) ||
    (currentKey === "parent_confirmation" && !session.parentEntityId);

  const onPatch = (patch: Partial<ContractorOnboardingDraft>) =>
    updateOnboardingDraft(session.id, patch, ACTOR);
  const onComplete = (stepKey: string, nextStep?: string) =>
    completeOnboardingStep(session.id, stepKey, ACTOR, nextStep);

  const handleUpload = async (type: OnboardingDocumentType, file: File) => {
    submitOnboardingDocument(
      session.id,
      { type, fileName: file.name, fileSize: file.size, mimeType: file.type },
      ACTOR,
    );
  };
  const handleSign = (input: {
    variant: NonNullable<OnboardingSession["signatures"]>[number]["variant"];
    version: string;
    typedName: string;
  }) => signOnboardingAgreement(session.id, input, ACTOR);
  const handleSetPayout = (input: {
    type: OnboardingPayoutType;
    last4?: string;
    parentEntityId?: string;
  }) =>
    setOnboardingPayout(
      session.id,
      { ...input, placeholderVerified: true },
      ACTOR,
    );

  const handleContinue = () => {
    if (continueDisabled || !currentKey) return;
    const next = getNextStepKey(session, currentKey);
    onComplete(currentKey, next);
    if (next) setLocalStepKey(next as OnboardingStepKey);
  };
  const handleBack = () => {
    if (readOnly || !currentKey) return;
    const prev = getPreviousStepKey(session, currentKey);
    if (prev) {
      onComplete(currentKey, prev);
      setLocalStepKey(prev as OnboardingStepKey);
    }
  };
  const handleSubmit = () => {
    try {
      setSubmitError(null);
      finalizeOnboardingSubmit(session.id, ACTOR);
    } catch (err) {
      setSubmitError((err as Error).message);
    }
  };

  if (session.status === "submitted") {
    return <SubmittedShell session={session} />;
  }

  // Lifecycle status (invitee perspective) + entity display.
  const lifecycleStage = deriveSessionLifecycleStage(session, "invitee");
  const entityName = (session.draft?.name ?? "").trim();

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-wizard-shell">
      <header className="border-b bg-card">
        <div className="mx-auto max-w-2xl px-6 py-4 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center text-xs font-semibold shrink-0">
                K
              </div>
              <div className="min-w-0">
                <div className="font-semibold tracking-tight text-sm truncate">
                  {entityName || "Koredor · Installer Onboarding"}
                </div>
                <div
                  className="text-[10px] text-muted-foreground flex items-center gap-1.5 flex-wrap"
                  data-testid="onboarding-wizard-header-meta"
                >
                  <span data-testid="onboarding-wizard-header-entity">
                    {session.entityType}
                  </span>
                  {session.parentEntityId ? (
                    <>
                      <span aria-hidden>·</span>
                      <span data-testid="onboarding-wizard-header-parent">
                        parent {session.parentEntityId}
                      </span>
                    </>
                  ) : null}
                  <span aria-hidden>·</span>
                  <span
                    className="font-medium text-foreground"
                    data-testid="onboarding-wizard-header-status"
                  >
                    {ONBOARDING_LIFECYCLE_LABELS[lifecycleStage]}
                  </span>
                </div>
              </div>
            </div>
            <span className="text-xs text-muted-foreground shrink-0">
              Step {Math.max(currentIndex, 0) + 1} of {steps.length}
            </span>
          </div>
          <OnboardingSavedIndicator />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-8 space-y-6">
        <ProgressBar
          total={steps.length}
          completed={session.completedSteps.length}
          currentIndex={Math.max(currentIndex, 0)}
        />

        {/* B3 — passive resume reassurance strip */}
        <div
          className="rounded-md border bg-muted/40 px-3 py-2 flex items-start gap-2"
          data-testid="onboarding-resume-reassurance"
        >
          <ShieldCheckIcon className="h-4 w-4 text-success mt-0.5 shrink-0" />
          <div className="text-xs leading-relaxed">
            <span className="font-medium text-foreground">
              Your progress is saved.
            </span>{" "}
            <span className="text-muted-foreground">
              You can safely return using this invite link.
            </span>
          </div>
        </div>

        {readOnly ? (
          <div
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
            data-testid="onboarding-observer-banner"
          >
            Observer mode — onboarding writes are disabled.
          </div>
        ) : null}

        {session.status === "reupload_requested" ? (
          <div
            className="rounded-lg border border-warning/40 bg-warning/10 p-4 space-y-3"
            data-testid="onboarding-reupload-banner"
          >
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full ring-1 ring-warning/40 bg-warning/15 text-warning grid place-items-center shrink-0">
                <RefreshCwIcon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground">
                  Reupload Requested
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Your administrator needs a few items refreshed before review can continue.
                </div>
              </div>
            </div>

            {session.reuploadReason ? (
              <div
                className="rounded-md border bg-card px-3 py-2 text-xs text-foreground"
                data-testid="onboarding-reupload-reason"
              >
                <span className="font-medium">Reason: </span>
                {session.reuploadReason}
              </div>
            ) : null}

            {(session.reuploadRequestedSteps ?? []).length > 0 ? (
              <div
                className="space-y-1.5"
                data-testid="onboarding-reupload-steps"
              >
                <div className="text-[11px] uppercase tracking-wide font-medium text-muted-foreground">
                  Steps to redo
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(session.reuploadRequestedSteps ?? []).map((k) => (
                    <Badge
                      key={k}
                      variant="outline"
                      className="bg-warning/15 text-warning-foreground border-warning/40 text-[11px] font-medium"
                      data-testid={`onboarding-reupload-chip-${k}`}
                    >
                      {getOnboardingStepLabel(k, session)}
                    </Badge>
                  ))}
                </div>
                {/* keep machine-readable joined label for legacy consumers */}
                <span className="sr-only">
                  {formatOnboardingStepKeys(
                    session.reuploadRequestedSteps ?? [],
                    session,
                  )}
                </span>
              </div>
            ) : null}

            <div className="flex items-center justify-end pt-1">
              <Button
                size="sm"
                disabled={readOnly}
                data-testid="onboarding-reupload-resume"
                onClick={() => {
                  const resume = getReuploadResumeStep(session);
                  if (resume) setLocalStepKey(resume as OnboardingStepKey);
                }}
              >
                Resume onboarding
                <ArrowRightIcon className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </div>
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle data-testid="onboarding-step-title">
              {currentDescriptor?.title ?? "Onboarding"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentKey === "identity" && (
              <IdentityStep session={session} readOnly={readOnly} onPatch={onPatch} />
            )}
            {currentKey === "service_area" && (
              <ServiceAreaStep session={session} readOnly={readOnly} onPatch={onPatch} />
            )}
            {currentKey === "documents" && (
              <DocumentsStep session={session} readOnly={readOnly} onUpload={handleUpload} />
            )}
            {currentKey === "agreement" && (
              <AgreementStep session={session} readOnly={readOnly} onSign={handleSign} />
            )}
            {currentKey === "payout" && (
              <PayoutStep session={session} readOnly={readOnly} onSet={handleSetPayout} />
            )}
            {currentKey === "parent_confirmation" && (
              <ParentConfirmationStep session={session} />
            )}
            {currentKey === "inherited_compliance_preview" && (
              <InheritedCompliancePreviewStep session={session} />
            )}
            {currentKey === "branch_overrides" && (
              <BranchOverridesStep session={session} readOnly={readOnly} onPatch={onPatch} />
            )}
            {currentKey === "minimal_info" && (
              <MinimalInfoStep session={session} readOnly={readOnly} onPatch={onPatch} />
            )}
            {currentKey === "review" && (
              <ReviewStep session={session} readiness={readiness} />
            )}
          </CardContent>
        </Card>

        {submitError ? (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            data-testid="onboarding-submit-error"
            role="alert"
          >
            {submitError}
          </div>
        ) : null}

        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={readOnly || currentIndex <= 0}
            data-testid="onboarding-back"
          >
            Back
          </Button>
          {currentKey === "review" ? (
            <Button
              onClick={handleSubmit}
              disabled={readOnly || !readiness.ready}
              data-testid="onboarding-submit"
            >
              Submit for review
            </Button>
          ) : (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={continueDisabled ? 0 : -1}>
                    <Button
                      onClick={handleContinue}
                      disabled={continueDisabled}
                      data-testid="onboarding-continue"
                      className="transition-all"
                    >
                      {isLast ? "Save & finish" : "Save & continue"}
                    </Button>
                  </span>
                </TooltipTrigger>
                {continueDisabled ? (
                  <TooltipContent
                    side="top"
                    data-testid="onboarding-continue-tooltip"
                  >
                    Complete the required fields above to continue.
                  </TooltipContent>
                ) : null}
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Need help? Contact your Koredor admin.
        </p>
      </main>
    </div>
  );
}
